import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled/macro';
import { Formik, Form } from 'formik';
import { Heading, Box, HStack, Radio } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { getAutoFormGenericAssetTemplates } from 'services/autoforms';
import { getItemTypeGenericAssetTemplates } from 'services/items';
import { getAutoformMetadata } from '../../store/autoform/autoformApi';
import AutomaticFormikDirtyCheck from '../forms/AutomaticFormikDirtyCheck';
import DefinitionsSkeleton from '../utils/DefinitionsSkeleton';
import RadioGroupFormikField from '../forms/RadioGroupFormikField';
import useItemCreationWizard from 'hooks/useItemCreationWizard';
import NavigationButtons from './CustomSubItemCreation/NavigationButtons';

const SubItemMethodDecisionStep = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [templates, setTemplates] = useState([]);
    const [showGenericAssetOption, setShowGenericAssetOption] = useState(true);

    const [isFormDirty, setIsFormDirty] = useState(false);
    const dispatch = useDispatch();

    const {
        itemTypeId: selectedItemTypeId,
        itemTypeName,
        autoForm: selectedAutoForm,
        moveToItemFormStep,
        moveToCustomSubItemStep,
        moveToGenericAssetStep,
    } = useItemCreationWizard();

    const handleSubmit = async (formValues) => {
        if (formValues.choice === '1') {
            moveToGenericAssetStep(templates);
        } else {
            moveToCustomSubItemStep();
        }
    };

    useEffect(() => {
        setIsLoading(true);
        async function fetchData() {
            if (selectedAutoForm !== null && selectedAutoForm !== '') {
                // Load autoform metadata in the background (don't await - it won't affect isLoading)
                dispatch(getAutoformMetadata(selectedAutoForm));

                await getAutoFormGenericAssetTemplates(selectedAutoForm)
                    .then((response) => {
                        if (Array.isArray(response?.data?.templates)) {
                            setTemplates(response?.data.templates);
                        } else {
                            setShowGenericAssetOption(false);
                        }
                    })
                    .finally(() => setIsLoading(false));
            } else {
                await getItemTypeGenericAssetTemplates(selectedItemTypeId)
                    .then((response) => {
                        if (Array.isArray(response?.data?.templates)) {
                            setTemplates(response?.data.templates);
                        } else {
                            setShowGenericAssetOption(false);
                        }
                    })
                    .finally(() => setIsLoading(false));
            }
            setIsLoading(false);
        }
        fetchData();
    }, [selectedAutoForm, selectedItemTypeId, dispatch]);

    const moveToPreviousStep = () => {
        moveToItemFormStep();
    };

    const initialFormValues = {
        choice: '0',
    };

    return (
        <>
            {isLoading ? (
                <CenteredWrapper>
                    <Heading pt={7} pb={0}>
                        <FormattedMessage
                            id="item_creation_wizard_step2_heading"
                            values={{ itemTypeName: itemTypeName }}
                        />
                    </Heading>
                    <DefinitionsSkeleton />
                </CenteredWrapper>
            ) : (
                <Formik
                    initialValues={initialFormValues}
                    onSubmit={handleSubmit}
                    enableReinitialize
                    validateOnChange={false}
                    validateOnBlur={false}
                >
                    {({ isSubmitting, errors, touched, values }) => (
                        <Form>
                            <CenteredWrapper>
                                <Heading pt={7} pb={0}>
                                    <FormattedMessage
                                        id="item_creation_wizard_step2_heading"
                                        values={{ itemTypeName: itemTypeName }}
                                    />
                                </Heading>
                                <AutomaticFormikDirtyCheck
                                    isFormDirty={isFormDirty}
                                    onFormDirtyChange={setIsFormDirty}
                                />
                                <RadioGroupFormikField pt={7} pb={0} columns={{ xl: 4 }} id="choice" name="choice">
                                    <HStack spacing={8}>
                                        <StyledRadio value={'0'}>
                                            <FormattedMessage id="item_creation_wizard_step2_custom_choice" />
                                        </StyledRadio>
                                        {showGenericAssetOption && (
                                            <StyledRadio value={'1'}>
                                                <FormattedMessage id="item_creation_wizard_step2_generic_asset_choice" />
                                            </StyledRadio>
                                        )}
                                    </HStack>
                                </RadioGroupFormikField>
                            </CenteredWrapper>
                            <NavigationButtons
                                isPrevStepDisabled={isSubmitting}
                                isConfirmSubmitting={isSubmitting}
                                isConfirmDisabled={isSubmitting}
                                handlePreviousStep={moveToPreviousStep}
                                openPreviousStepModal={false}
                                displayFinishText={false}
                            />
                        </Form>
                    )}
                </Formik>
            )}
        </>
    );
};

const CenteredWrapper = styled(Box)`
    width: 100%;
    max-width: 1314px;
    margin: 0 auto;
    padding: 0 16px;
`;

const StyledRadio = styled(Radio)`
    margin-right: 2px;
    border-color: var(--chakra-colors-green-500);

    &:hover {
        border-color: var(--chakra-colors-blue-400);
    }
`;

export default SubItemMethodDecisionStep;
